import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../../config/firebase';
import {
  doc, onSnapshot, updateDoc, deleteDoc, serverTimestamp,
  runTransaction, increment, arrayUnion, setDoc, getDoc, addDoc, collection
} from 'firebase/firestore';
import { Chess } from 'chess.js';
import ChessBoard from './ChessBoard';
import Icon from '../Shared/Icons';
import { calculateGameRatings, getTier, DEFAULT_RATING, activityBonus } from '../../utils/chessRating';
import { getLifeLesson } from '../../utils/chessLifeLessons';
import { getBotMove, getBotDifficulty } from '../../utils/chessBot';
import { recordTournamentGameResult } from '../../utils/tournamentEngine';
import { detectOpening } from '../../utils/chessOpenings';
import { sfx, isMuted, toggleMute } from '../../utils/chessSounds';
import { getCapturedPieces } from '../../utils/chessCaptures';
import { getBoardTheme, getPieceTheme, nextBoardTheme, nextPieceTheme } from '../../utils/chessThemes';
import { C } from '../../utils/chessTheme';

const PLAYER_KEY = 'iyanna_chess_player';

function loadPlayer() {
  try {
    const raw = localStorage.getItem(PLAYER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function fmtClock(seconds) {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, '0')}`;
}

export default function ChessRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  // IMPORTANT: loadPlayer() must run ONCE. Calling it on every render
  // created a brand-new object each time, which made the main useEffect
  // (deps: [id, player, navigate]) re-run on EVERY render — resetting
  // manualFlip (flip button "didn't work"), wiping localResult (win
  // message looked delayed), and re-subscribing onSnapshot in a loop
  // (the page "shaking" after refresh).
  const [player] = useState(loadPlayer);

  const [game, setGame] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [, setTick] = useState(0); // forces re-render for live clock display
  const [botThinking, setBotThinking] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [localResult, setLocalResult] = useState(null); // { winner, reason } — shown instantly, ahead of Firestore sync
  const [pendingFen, setPendingFen] = useState(null); // locks the board the instant a move is made, until Firestore confirms it
  const [hint, setHint] = useState(null); // { san, from, to }
  const [hintsLeft, setHintsLeft] = useState(3); // coach mode: 3 hints per bot game
  const [manualFlip, setManualFlip] = useState(false);
  const [muted, setMuted] = useState(isMuted());
  const [showMoveList, setShowMoveList] = useState(false);
  const [boardTheme, setBoardTheme] = useState(getBoardTheme());
  const [pieceTheme, setPieceTheme] = useState(getPieceTheme());
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState(null); // { text, kind }
  const prevMoveCountRef = useRef(null);
  const resultSoundPlayedRef = useRef(false);
  const ratingSyncedRef = useRef(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved

  async function handleSaveGame() {
    if (!game || saveState !== 'idle') return;
    setSaveState('saving');
    try {
      await updateDoc(doc(db, 'chessGames', id), {
        isSaved: true,
        isPrivate: false, // make it visible in the public replay feed
        startFen: new Chess().fen(),
      });
      setSaveState('saved');
    } catch (e) { console.error(e); setSaveState('idle'); }
  }

  const [rematchBusy, setRematchBusy] = useState(false);

  // Rematch: create a fresh game with the SAME two players, colours
  // swapped, same time control and privacy. The old game stores the new
  // game's id so the opponent (still on the result screen) can join too.
  async function handleRematch() {
    if (!game) return;
    if (game.isBotGame) { navigate('/chess'); return; }
    if (game.rematchGameId) { navigate(`/chess/room/${game.rematchGameId}`); return; }
    setRematchBusy(true);
    try {
      const clock = game.clockSeconds || 600;
      const startFen = new Chess().fen();
      const ref = await addDoc(collection(db, 'chessGames'), {
        isPrivate: !!game.isPrivate,
        isClub: !!game.isClub,
        inviteCode: null,
        status: 'active',
        fen: startFen,
        turn: 'w',
        white: game.black,   // swap colours
        black: game.white,
        winner: null,
        resultReason: null,
        clockSeconds: clock,
        whiteTimeLeft: clock,
        blackTimeLeft: clock,
        turnStartedAt: serverTimestamp(),
        lastFrom: null,
        lastTo: null,
        moveHistory: [],
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'chessGames', id), { rematchGameId: ref.id });
      navigate(`/chess/room/${ref.id}`);
    } catch (e) { console.error(e); }
    setRematchBusy(false);
  }
  const moveListEndRef = useRef(null);

  useEffect(() => {
    if (!player) { navigate('/chess'); return; }
    setLocalResult(null);
    setPendingFen(null);
    setHint(null);
    setHintsLeft(3);
    setManualFlip(false);
    prevMoveCountRef.current = null;
    resultSoundPlayedRef.current = false;
    ratingSyncedRef.current = false;
    const unsub = onSnapshot(doc(db, 'chessGames', id), snap => {
      if (!snap.exists()) { setNotFound(true); return; }
      // 'estimate' is the CLOCK FIX: turnStartedAt is written with
      // serverTimestamp(), and until the server confirms it, a plain
      // snap.data() returns it as NULL. On slow connections that meant
      // (a) the displayed clock fell back to the full base time (looked
      // like it restarted every move) and (b) the next move computed
      // elapsed = 0, so time was never actually deducted. With
      // 'estimate', pending timestamps resolve to a local estimate
      // immediately, so the clock keeps counting smoothly.
      setGame({ id: snap.id, ...snap.data({ serverTimestamps: 'estimate' }) });
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Move sound — fires whenever a new move lands, regardless of who made
  // it (own move, bot's reply, or the opponent's move arriving via sync).
  useEffect(() => {
    const count = game?.moveHistory?.length || 0;
    if (prevMoveCountRef.current !== null && count > prevMoveCountRef.current) {
      const last = game.moveHistory[count - 1];
      if (last?.san?.includes('x')) sfx.capture();
      else if (last?.san?.includes('+') || last?.san?.includes('#')) sfx.check();
      else sfx.move();
    }
    prevMoveCountRef.current = count;
    if (moveListEndRef.current) {
      moveListEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [game?.moveHistory?.length]);

  // Win/lose/draw sound — fires once, as soon as a result is known
  // (instantly via localResult, or from Firestore sync for spectators).
  useEffect(() => {
    const result = localResult || (game?.status === 'finished' ? { winner: game.winner } : null);
    if (result && !resultSoundPlayedRef.current) {
      resultSoundPlayedRef.current = true;
      const iWon = player && result.winner !== 'draw' &&
        ((game?.white?.id === player.id && result.winner === 'white') ||
         (game?.black?.id === player.id && result.winner === 'black'));
      if (result.winner === 'draw') sfx.draw();
      else if (iWon) sfx.win();
      else sfx.lose();
    }
  }, [localResult, game?.status]);

  // Rating sync + leaderboard — when a game finishes with published
  // ratings, EACH client updates its own local profile and its own
  // entry in the public chessPlayers leaderboard. Guarded so it runs
  // exactly once per game per client.
  useEffect(() => {
    if (ratingSyncedRef.current) return;
    if (!player || game?.status !== 'finished') return;
    if (game.newWhiteRating == null || game.newBlackRating == null) return;
    const mine =
      game.white?.id === player.id ? game.newWhiteRating :
      game.black?.id === player.id ? game.newBlackRating : null;
    if (mine == null) return;
    ratingSyncedRef.current = true;
    try {
      const updated = { ...player, rating: mine };
      localStorage.setItem(PLAYER_KEY, JSON.stringify(updated));
    } catch {}
    // Upsert my leaderboard entry (self-chosen nickname only — safe to
    // show publicly), tracking TODAY'S net rating movement so the live
    // ranking shows daily form (▲/▼) for every player.
    (async () => {
      try {
        const delta = mine - (player.rating || 0);
        // Was this a "meeting" game (tournament or club)?
        const wasMeet = !!game.tournamentId || !!game.isClub;
        // Did I win / lose / draw? (for a truthful public record)
        const myColor = game.white?.id === player.id ? 'white' : 'black';
        const iWon = game.winner === myColor;
        const iDrew = game.winner === 'draw';
        const iLost = game.winner && !iWon && !iDrew;
        const today = new Date().toISOString().slice(0, 10);
        // Week key = the Monday of this week (so the whole week shares one id)
        const monday = new Date();
        monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
        const week = monday.toISOString().slice(0, 10);
        const ref = doc(db, 'chessPlayers', player.id);
        let todayDelta = delta;
        let gamesToday = 1;
        let weekDelta = delta;
        try {
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const d = snap.data();
            if (d.dayKey === today) {
              todayDelta = (d.todayDelta || 0) + delta;
              gamesToday = (d.gamesToday || 0) + 1;
            }
            if (d.weekKey === week) {
              weekDelta = (d.weekDelta || 0) + delta;
            }
          }
        } catch {}
        await setDoc(ref, {
          name: player.name,
          nameLower: (player.name || '').toLowerCase(),
          rating: mine,
          dayKey: today,
          todayDelta,
          gamesToday,
          weekKey: week,
          weekDelta,
          meetGames: increment(wasMeet ? 1 : 0),
          gamesPlayed: increment(1),
          wins: increment(iWon ? 1 : 0),
          losses: increment(iLost ? 1 : 0),
          draws: increment(iDrew ? 1 : 0),
          updatedAt: serverTimestamp(),
        }, { merge: true });

        // Hall of Fame — contend for this week's champion slot. The doc
        // for each week freezes once the week passes: permanent history.
        if (weekDelta > 0) {
          try {
            const weekRef = doc(db, 'chessWeekly', week);
            const weekSnap = await getDoc(weekRef);
            const best = weekSnap.exists() ? (weekSnap.data().bestDelta || 0) : 0;
            if (weekDelta > best) {
              await setDoc(weekRef, {
                weekKey: week,
                championId: player.id,
                championName: player.name,
                bestDelta: weekDelta,
                rating: mine,
                updatedAt: serverTimestamp(),
              }, { merge: true });
            }
          } catch (e) { console.error(e); }
        }
      } catch (e) { console.error(e); }
    })();
  }, [game?.status, game?.newWhiteRating, game?.newBlackRating]);

  // live clock ticker
  useEffect(() => {
    if (!game || game.status !== 'active') return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [game?.status]);

  // ── SKEW-FREE TURN TIMER ─────────────────────────────────────────────
  // elapsed used to be computed as (device Date.now()) − (SERVER
  // timestamp). If the phone's clock differs from Firebase's clock by
  // even 30–60s (very common), every move computed elapsed ≈ 0 (time
  // never deducted — the clock looked like it RESTARTED each move) or
  // overcharged every single move. Fix: each client anchors the start of
  // every turn it OBSERVES using its OWN clock, so start and "now" come
  // from the same clock. The server timestamp is only a fallback right
  // after a page refresh, before any turn change has been seen live.
  const turnAnchorRef = useRef(null); // { key, localMs, observed }
  useEffect(() => {
    if (!game) return;
    if (game.status !== 'active' || game.paused) {
      // Clear while paused/inactive so the next active turn re-anchors
      // freshly — otherwise elapsed would include the paused period.
      turnAnchorRef.current = null;
      return;
    }
    const key = `${(game.moveHistory || []).length}`;
    const prev = turnAnchorRef.current;
    if (!prev || prev.key !== key) {
      turnAnchorRef.current = { key, localMs: Date.now(), observed: !!prev };
    }
  }, [game?.moveHistory?.length, game?.status, game?.paused]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // Announce opponent draw offers as toasts
  useEffect(() => {
    if (!game?.drawOfferedBy || !player) return;
    const myC = game.white?.id === player.id ? 'w' : game.black?.id === player.id ? 'b' : null;
    if (myC && game.drawOfferedBy !== myC) {
      showToast('Mpinzani amependekeza sare', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.drawOfferedBy]);

  // Remember which color I played in this (human vs human) game so the
  // lobby can give me the OPPOSITE color next game — white, then black,
  // then white… alternating for as long as they keep playing.
  useEffect(() => {
    if (!player || !game || game.isBotGame) return;
    if (game.status !== 'active' && game.status !== 'finished') return;
    const c = game.white?.id === player.id ? 'white' : game.black?.id === player.id ? 'black' : null;
    if (c) {
      try { localStorage.setItem('iyanna_chess_last_color', c); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.status]);

  // Once Firestore confirms our move (fen matches), release the lock.
  useEffect(() => {
    if (pendingFen && game?.fen === pendingFen) {
      setPendingFen(null);
    }
  }, [game?.fen, pendingFen]);

  // Safety net — never leave the board locked forever if sync is slow.
  useEffect(() => {
    if (!pendingFen) return;
    const t = setTimeout(() => setPendingFen(null), 8000);
    return () => clearTimeout(t);
  }, [pendingFen]);

  // timeout watcher — whoever's browser notices a flag fall reports it
  useEffect(() => {
    if (!game || game.status !== 'active' || !game.turnStartedAt) return;
    const elapsedSince = turnElapsedSeconds();
    const movingColor = game.turn;
    const baseTime = movingColor === 'w' ? game.whiteTimeLeft : game.blackTimeLeft;
    const remaining = baseTime - elapsedSince;
    if (remaining <= 0) {
      finishGame(movingColor === 'w' ? 'black' : 'white', 'timeout');
    }
  });

  // bot's turn — compute and play its move automatically. The bot can be
  // EITHER color now (the player chooses their side in the lobby), so we
  // detect the bot's seat instead of assuming it plays black.
  const botColorChar = game?.isBotGame
    ? (game.white?.isBot ? 'w' : 'b')
    : null;
  const humanRatingForBot = game?.isBotGame
    ? ((botColorChar === 'w' ? game.black?.rating : game.white?.rating) || DEFAULT_RATING)
    : DEFAULT_RATING;

  useEffect(() => {
    if (!game || !game.isBotGame || game.status !== 'active' || game.paused || game.turn !== botColorChar || botThinking) return;
    const diff = getBotDifficulty(humanRatingForBot);
    setBotThinking(true);
    const timer = setTimeout(() => {
      playBotMove();
    }, diff.thinkMs);
    return () => {
      clearTimeout(timer);
      // If this effect gets cleaned up before the timer fires, the bot
      // never actually moved — don't leave it permanently stuck waiting.
      setBotThinking(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.fen, game?.turn, game?.status, game?.isBotGame]);

  async function playBotMove() {
    try {
      const botMove = getBotMove(game.fen, humanRatingForBot);
      if (!botMove) { setBotThinking(false); return; }

      const sim = new Chess(game.fen);
      const applied = sim.move({ from: botMove.from, to: botMove.to, promotion: botMove.promotion || 'q' });
      if (!applied) { setBotThinking(false); return; }

      let winner = null;
      if (sim.isCheckmate()) winner = botColorChar === 'w' ? 'white' : 'black';
      else if (sim.isDraw() || sim.isStalemate()) winner = 'draw';

      await handleMove({
        fen: sim.fen(),
        san: applied.san,
        from: botMove.from,
        to: botMove.to,
        isGameOver: sim.isGameOver(),
        isCheckmate: sim.isCheckmate(),
        isDraw: sim.isDraw(),
        isStalemate: sim.isStalemate(),
        isCheck: sim.isCheck(),
        winner,
        turn: sim.turn(),
      });
    } catch (e) {
      console.error(e);
    }
    setBotThinking(false);
  }

  if (!player) return null;
  if (notFound) {
    return (
      <div style={styles.page}>
        <div style={styles.centerMsg}>
          Mchezo huu haupatikani. <Link to="/chess" style={styles.link}>Rudi Lobby</Link>
        </div>
      </div>
    );
  }
  if (!game) {
    return <div style={styles.page}><div style={styles.centerMsg}>Inapakia...</div></div>;
  }

  const isWhite = game.white?.id === player.id;
  const isBlack = game.black?.id === player.id;
  const isPlayer = isWhite || isBlack;
  const myColor = isWhite ? 'w' : isBlack ? 'b' : null;
  const baseOrientation = isBlack ? 'black' : 'white';
  const orientation = manualFlip ? (baseOrientation === 'white' ? 'black' : 'white') : baseOrientation;
  const interactive = isPlayer && game.status === 'active' && !game.paused && game.turn === myColor && !pendingFen;
  const { capturedByWhite, capturedByBlack, materialDiff } = getCapturedPieces(game.fen);

  const liveWhiteTime = computeLiveTime('w');
  const liveBlackTime = computeLiveTime('b');

  // Seconds since the current turn started, measured on ONE clock.
  function turnElapsedSeconds() {
    const a = turnAnchorRef.current;
    if (a && a.observed) return Math.max(0, (Date.now() - a.localMs) / 1000);
    if (game?.turnStartedAt) return Math.max(0, (Date.now() - game.turnStartedAt.toMillis()) / 1000);
    return 0;
  }

  function computeLiveTime(color) {
    const base = color === 'w' ? game.whiteTimeLeft : game.blackTimeLeft;
    if (game.status !== 'active' || game.turn !== color || !game.turnStartedAt) return base;
    return Math.max(0, base - turnElapsedSeconds());
  }

  async function handleMove(moveResult) {
    if (!isPlayer) return;
    // Lock immediately — closes the window where a fast double-tap could
    // submit a second move before Firestore confirms the first one.
    setPendingFen(moveResult.fen);
    setHint(null);
    try {
      const movingColor = game.turn; // color that just moved
      const elapsed = turnElapsedSeconds();
      const updates = {
        fen: moveResult.fen,
        turn: moveResult.turn,
        lastFrom: moveResult.from,
        lastTo: moveResult.to,
        turnStartedAt: serverTimestamp(),
        moveHistory: arrayUnion({
          san: moveResult.san,
          from: moveResult.from,
          to: moveResult.to,
          fen: moveResult.fen,
          color: movingColor,
        }),
      };
      if (movingColor === 'w') {
        updates.whiteTimeLeft = Math.max(0, game.whiteTimeLeft - elapsed);
      } else {
        updates.blackTimeLeft = Math.max(0, game.blackTimeLeft - elapsed);
      }

      if (moveResult.isGameOver) {
        // Show the result INSTANTLY — chess.js already knows the game is
        // over right now, no need to wait on a network round-trip.
        let reason = 'checkmate';
        if (moveResult.isStalemate) reason = 'stalemate';
        else if (moveResult.isDraw) reason = 'draw';
        setLocalResult({ winner: moveResult.winner, reason });
      }

      // Fire both writes without blocking each other — the move-history
      // update and the finish-game write no longer wait in sequence,
      // which was the source of the visible delay.
      const moveWrite = updateDoc(doc(db, 'chessGames', id), updates);
      if (moveResult.isGameOver) {
        let reason = 'checkmate';
        if (moveResult.isStalemate) reason = 'stalemate';
        else if (moveResult.isDraw) reason = 'draw';
        await Promise.all([moveWrite, finishGame(moveResult.winner, reason)]);
      } else {
        await moveWrite;
      }
    } catch (e) {
      console.error(e);
      setPendingFen(null); // don't leave the board stuck locked if the write failed
    }
  }

  async function finishGame(winner, reason) {
    // Instant local feedback for resign/timeout paths too.
    setLocalResult(prev => prev || { winner, reason });
    try {
      await runTransaction(db, async (tx) => {
        const ref = doc(db, 'chessGames', id);
        const snap = await tx.get(ref);
        if (!snap.exists()) return;
        const data = snap.data();
        if (data.status === 'finished') return; // already processed, avoid double-rating

        tx.update(ref, {
          status: 'finished',
          winner,
          resultReason: reason,
          finishedAt: serverTimestamp(),
        });

        // Rating updates only apply to real games with two players
        if (data.white && data.black) {
          // Context decides how many points move: bot games barely move
          // the needle, human games move it a lot, tournaments the most.
          const context = data.isBotGame ? 'bot' : (data.tournamentId ? 'tournament' : 'human');
          const { newWhiteRating, newBlackRating } = calculateGameRatings(
            data.white.rating || DEFAULT_RATING,
            data.black.rating || DEFAULT_RATING,
            winner,
            context
          );

          // ACTIVITY BONUS — reward meeting in person. Tournament games and
          // club (face-to-face) games grant extra points to BOTH players on
          // top of the Elo result, so those who gather and compete climb
          // faster. Applied here so it lands in the published ratings that
          // every client syncs from.
          const bonusContext = data.tournamentId ? 'tournament' : (data.isClub ? 'club' : context);
          const bonus = activityBonus(bonusContext);
          const finalWhite = newWhiteRating + bonus;
          const finalBlack = newBlackRating + bonus;

          // Publish the new ratings on the game doc itself — this lets
          // EVERY client (not just the one that reported the result)
          // sync its own local rating and leaderboard entry.
          tx.update(ref, { newWhiteRating: finalWhite, newBlackRating: finalBlack, activityBonusApplied: bonus });

          if (data.white.studentId) {
            const wRef = doc(db, 'chessStudents', data.white.studentId);
            tx.update(wRef, {
              rating: finalWhite,
              wins: increment(winner === 'white' ? 1 : 0),
              losses: increment(winner === 'black' ? 1 : 0),
              draws: increment(winner === 'draw' ? 1 : 0),
              gamesPlayed: increment(1),
              meetGames: increment(bonus > 0 ? 1 : 0),
            });
          }
          if (data.black.studentId) {
            const bRef = doc(db, 'chessStudents', data.black.studentId);
            tx.update(bRef, {
              rating: finalBlack,
              wins: increment(winner === 'black' ? 1 : 0),
              losses: increment(winner === 'white' ? 1 : 0),
              draws: increment(winner === 'draw' ? 1 : 0),
              gamesPlayed: increment(1),
              meetGames: increment(bonus > 0 ? 1 : 0),
            });
          }

          // Keep the local guest profile's rating in sync too
          if (player) {
            const mine = data.white?.id === player.id
              ? { color: 'white', rating: finalWhite }
              : data.black?.id === player.id
                ? { color: 'black', rating: finalBlack }
                : null;
            if (mine) {
              const updated = { ...player, rating: mine.rating };
              localStorage.setItem(PLAYER_KEY, JSON.stringify(updated));
            }
          }
        }
      });

      // Tournament bookkeeping (pairing result + Swiss score updates)
      // happens outside the transaction since it touches a different
      // subcollection tree.
      if (game.isTournament) {
        await recordTournamentGameResult({
          tournamentId: game.tournamentId,
          roundId: game.roundId,
          pairingId: game.pairingId,
          winner,
          isTournament: true,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  function handleHint() {
    if (!game?.isBotGame || hintsLeft <= 0 || !interactive) return;
    try {
      // Reuse the strongest engine settings for the suggestion — the coach
      // should always suggest a genuinely good move regardless of bot level.
      const suggestion = getBotMove(game.fen, 1800);
      if (suggestion) {
        setHint({ san: suggestion.san, from: suggestion.from, to: suggestion.to });
        setHintsLeft(h => h - 1);
      }
    } catch (e) { console.error(e); }
  }

  // ── Draw offer ──────────────────────────────────────────────────────
  function showToast(text, kind = 'info') {
    setToast({ text, kind, id: Date.now() });
  }

  async function handleOfferDraw() {
    if (!isPlayer || game.status !== 'active' || game.isBotGame) return;
    await updateDoc(doc(db, 'chessGames', id), { drawOfferedBy: myColor });
    showToast('Umetuma pendekezo la sare', 'info');
  }
  async function handleAcceptDraw() {
    await finishGame('draw', 'draw');
    await updateDoc(doc(db, 'chessGames', id), { drawOfferedBy: null });
  }
  async function handleDeclineDraw() {
    await updateDoc(doc(db, 'chessGames', id), { drawOfferedBy: null });
  }

  // ── Takeback ─────────────────────────────────────────────────────────
  async function handleBotUndo() {
    if (!game.isBotGame || game.status !== 'active') return;
    const history = game.moveHistory || [];
    if (history.length === 0) return;
    // Pop moves until it is the HUMAN's turn again — works whether the
    // human is playing white or black against the bot.
    const humanChar = botColorChar === 'w' ? 'b' : 'w';
    const newHistory = [...history];
    while (newHistory.length > 0) {
      newHistory.pop();
      const fenNow = newHistory.length ? newHistory[newHistory.length - 1].fen : new Chess().fen();
      if (new Chess(fenNow).turn() === humanChar) break;
      if (newHistory.length === 0) break;
    }
    const last = newHistory[newHistory.length - 1];
    await updateDoc(doc(db, 'chessGames', id), {
      moveHistory: newHistory,
      fen: last ? last.fen : new Chess().fen(),
      turn: last ? new Chess(last.fen).turn() : 'w',
      lastFrom: last ? last.from : null,
      lastTo: last ? last.to : null,
      turnStartedAt: serverTimestamp(),
    });
    sfx.notify();
  }
  async function handleRequestTakeback() {
    if (!isPlayer || game.status !== 'active' || game.isBotGame) return;
    const history = game.moveHistory || [];
    if (history.length === 0) return;
    await updateDoc(doc(db, 'chessGames', id), { takebackRequestedBy: myColor });
    showToast('Umeomba kurudisha move', 'info');
  }
  async function handleAcceptTakeback() {
    const history = game.moveHistory || [];
    if (history.length === 0) return;
    const newHistory = history.slice(0, -1);
    const last = newHistory[newHistory.length - 1];
    await updateDoc(doc(db, 'chessGames', id), {
      moveHistory: newHistory,
      fen: last ? last.fen : new Chess().fen(),
      turn: game.takebackRequestedBy,
      lastFrom: last ? last.from : null,
      lastTo: last ? last.to : null,
      turnStartedAt: serverTimestamp(),
      takebackRequestedBy: null,
    });
    sfx.notify();
  }
  async function handleDeclineTakeback() {
    await updateDoc(doc(db, 'chessGames', id), { takebackRequestedBy: null });
  }

  async function handlePause() {
    if (!game?.isBotGame || game.status !== 'active' || game.paused) return;
    try {
      await updateDoc(doc(db, 'chessGames', id), {
        paused: true,
        whiteTimeLeft: computeLiveTime('w'),
        blackTimeLeft: computeLiveTime('b'),
        turnStartedAt: null,
      });
    } catch (e) { console.error(e); }
  }

  async function handleResume() {
    if (!game?.isBotGame || !game.paused) return;
    try {
      await updateDoc(doc(db, 'chessGames', id), {
        paused: false,
        turnStartedAt: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
  }

  async function handleResign() {
    if (!isPlayer || game.status !== 'active') return;
    if (!window.confirm('Una uhakika unataka ku-resign?')) return;
    await finishGame(myColor === 'w' ? 'black' : 'white', 'resignation');
  }

  async function handleQuit() {
    if (!isPlayer) return;
    if (game.isBotGame) {
      // Solo practice game — safe to just delete, no rating impact on anyone.
      if (!window.confirm('Ondoka kwenye mchezo huu na Bot? Hautahesabiwa.')) return;
      try {
        await deleteDoc(doc(db, 'chessGames', id));
      } catch (e) { console.error(e); }
      navigate('/chess');
      return;
    }
    if (game.status === 'waiting') {
      // Nobody has joined yet — safe to cancel outright.
      if (!window.confirm('Ghairi mchezo huu unaosubiri mpinzani?')) return;
      try {
        await deleteDoc(doc(db, 'chessGames', id));
      } catch (e) { console.error(e); }
      navigate('/chess');
      return;
    }
    // A real opponent is mid-game — quitting without resigning would be
    // unfair to them, so we just leave the room (their clock keeps
    // running and they can claim a win on time if you don't come back).
    navigate('/chess');
  }

  const whiteTier = getTier(game.white?.rating || DEFAULT_RATING);
  const blackTier = game.black ? getTier(game.black.rating || DEFAULT_RATING) : null;

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes iyannaConfettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(420px) rotate(540deg); opacity: 0; }
        }
        @keyframes iyannaResultPop {
          0% { transform: scale(0.86); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div style={styles.topBar}>
        <button style={styles.backIconBtn} onClick={() => navigate('/chess')}>
          <Icon name="door" size={19} color={C.text} strokeWidth={2.4} />
        </button>

        <div style={styles.topCenter}>
          {game.isTournament && <span style={styles.chip}>Mashindano</span>}
          {game.isClub && <span style={styles.chip}>Klabu</span>}
          {game.isBotGame && <span style={styles.chip}>{game.botLabel || 'Bot'}</span>}
          {game.isPrivate && game.status === 'waiting' && !game.isBotGame && isPlayer && (
            <span style={styles.codeChip}>Code: <strong>{game.inviteCode}</strong></span>
          )}
          {!isPlayer && <span style={styles.chip}>Unatazama</span>}
        </div>

        {!game.isBotGame ? (
          <button
            style={styles.backIconBtn}
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setLinkCopied(true);
              setTimeout(() => setLinkCopied(false), 2000);
            }}
            title="Nakili link"
          >
            <Icon name={linkCopied ? 'check' : 'link'} size={18} color={linkCopied ? C.accent : C.text} strokeWidth={2.2} />
          </button>
        ) : <div style={{ width: '38px' }} />}
      </div>

      <div style={styles.roomLayout}>
        {/* Opponent panel (top) */}
        <PlayerPanel
          name={orientation === 'white' ? game.black?.name : game.white?.name}
          rating={orientation === 'white' ? game.black?.rating : game.white?.rating}
          tier={orientation === 'white' ? blackTier : whiteTier}
          time={orientation === 'white' ? liveBlackTime : liveWhiteTime}
          isTurn={game.turn === (orientation === 'white' ? 'b' : 'w')}
          placeholder={game.status === 'waiting' ? 'Waiting for opponent…' : null}
          captured={orientation === 'white' ? capturedByBlack : capturedByWhite}
          materialEdge={orientation === 'white' ? Math.max(0, -materialDiff) : Math.max(0, materialDiff)}
        />

        <div style={styles.boardWrapper}>
          {game.paused && game.status === 'active' && (
            <div style={styles.pausedOverlay}>
              <div style={styles.pausedCard}>
                <div style={styles.pausedTitle}>Mchezo Umesimamishwa</div>
                <div style={styles.pausedSub}>Saa zimesimama. Rudi wakati wowote — mchezo unakusubiri.</div>
                <button style={styles.resumeBtn} onClick={handleResume}>▶ Endelea Kucheza</button>
              </div>
            </div>
          )}
          <ChessBoard
            fen={game.fen}
            onMove={handleMove}
            boardTheme={boardTheme}
            pieceTheme={pieceTheme}
            interactive={interactive}
            playerColor={myColor}
            orientation={orientation}
            lastMove={game.lastFrom ? { from: game.lastFrom, to: game.lastTo } : null}
          />

          {/* RESULT MODAL — pops up centered over the board only, shown
              instantly via localResult so it never waits on network sync */}
          {(localResult || game.status === 'finished') && (() => {
            const winner = localResult?.winner ?? game.winner;
            const resultReason = localResult?.reason ?? game.resultReason;
            const isWinner = isPlayer && winner !== 'draw' && (
              (isWhite && winner === 'white') || (isBlack && winner === 'black')
            );
            const isLoser = isPlayer && winner !== 'draw' && !isWinner;
            const heading = winner === 'draw'
              ? 'Ni Sare! (Draw)'
              : isWinner
              ? 'Umeshinda!'
              : isLoser
              ? 'Umeshindwa'
              : `${winner === 'white' ? game.white?.name : game.black?.name} Ameshinda`;

            const lesson = getLifeLesson(
              winner,
              isWhite ? 'white' : isBlack ? 'black' : null,
              resultReason
            );

            return (
              <div style={styles.resultOverlay}>
                {isWinner && (
                  <div style={styles.confettiWrap}>
                    {Array.from({ length: 26 }).map((_, i) => (
                      <span
                        key={i}
                        style={{
                          ...styles.confettiPiece,
                          left: `${(i * 37) % 100}%`,
                          background: ['#10B981', '#34D399', '#D9F5E7', '#10B981', '#fff'][i % 5],
                          animationDelay: `${(i % 10) * 0.12}s`,
                          animationDuration: `${1.6 + (i % 5) * 0.25}s`,
                        }}
                      />
                    ))}
                  </div>
                )}
                <div style={styles.resultModal}>
                  <div style={styles.resultTitle}>{heading}</div>
                  <div style={styles.resultSubtitle}>
                    {winner !== 'draw' && (
                      isWinner ? `You beat ${isWhite ? game.black?.name : game.white?.name}` :
                      isLoser ? `${winner === 'white' ? game.white?.name : game.black?.name} won this game` :
                      `${winner === 'white' ? game.white?.name : game.black?.name} defeated ${winner === 'white' ? game.black?.name : game.white?.name}`
                    )}
                  </div>
                  <div style={styles.resultReason}>
                    {resultReason === 'checkmate' && 'by checkmate '}
                    {resultReason === 'resignation' && (isLoser ? 'you resigned ' : isWinner ? 'opponent resigned ' : 'by resignation ')}
                    {resultReason === 'timeout' && 'on time '}
                    {resultReason === 'stalemate' && 'stalemate'}
                    {resultReason === 'draw' && 'draw agreed / repetition'}
                  </div>

                  {/* Attendance bonus note — meeting games grant extra points */}
                  {game.activityBonusApplied > 0 && (
                    <div style={styles.bonusNote}>
                      +{game.activityBonusApplied} pointi za ziada kwa kucheza {game.tournamentId ? 'mashindano' : 'klabu'} 🤝
                    </div>
                  )}

                  {/* UNIQUE FEATURE — Somo la Maisha (Life Lesson) */}
                  <div style={styles.lessonCard}>
                    <div style={styles.lessonBadge}>Somo la Maisha · {lesson.skill}</div>
                    <div style={styles.lessonSwahili}>{lesson.swahili}</div>
                    <div style={styles.lessonEnglish}>{lesson.english}</div>
                  </div>

                  {/* SAVE CHOICE — the player decides if this game is kept
                      as a public replay others can watch, or stays private */}
                  {isPlayer && !game.isBotGame && (
                    <div style={styles.saveRow}>
                      <button
                        style={{ ...styles.saveBtn, ...(saveState === 'saved' ? styles.saveBtnDone : {}) }}
                        disabled={saveState === 'saving' || saveState === 'saved'}
                        onClick={handleSaveGame}
                      >
                        <Icon name={saveState === 'saved' ? 'check' : 'play'} size={15} color="#06281A" strokeWidth={2.4} />
                        {saveState === 'saved' ? 'Imehifadhiwa — watu wanaweza kuangalia' : saveState === 'saving' ? 'Inahifadhi…' : 'Hifadhi (watu waangalie)'}
                      </button>
                      <div style={styles.saveHint}>Usipohifadhi, mchezo wako unabaki wako binafsi.</div>
                    </div>
                  )}

                  {/* Opponent started a rematch — offer to join it */}
                  {game.rematchGameId && isPlayer && (
                    <div style={styles.rematchReady}>
                      <button style={styles.newGameBtn} onClick={() => navigate(`/chess/room/${game.rematchGameId}`)}>
                        Mpinzani anataka tena — Jiunge →
                      </button>
                    </div>
                  )}

                  <div style={styles.resultBtnRow}>
                    <button style={styles.reviewBtn} onClick={() => navigate(`/chess/review/${game.id}`)}>
                      Kagua Mchezo
                    </button>
                    {game.isBotGame ? (
                      <button style={styles.newGameBtn} onClick={() => navigate('/chess')}>
                        Cheza Tena →
                      </button>
                    ) : (
                      <button style={styles.newGameBtn} disabled={rematchBusy} onClick={handleRematch}>
                        {rematchBusy ? 'Inaanza…' : game.rematchGameId ? 'Nenda Kwenye Mchezo →' : 'Cheza Tena →'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* My panel (bottom) */}
        <PlayerPanel
          name={orientation === 'white' ? game.white?.name : game.black?.name}
          rating={orientation === 'white' ? game.white?.rating : game.black?.rating}
          tier={orientation === 'white' ? whiteTier : blackTier}
          time={orientation === 'white' ? liveWhiteTime : liveBlackTime}
          isTurn={game.turn === (orientation === 'white' ? 'w' : 'b')}
          captured={orientation === 'white' ? capturedByWhite : capturedByBlack}
          materialEdge={orientation === 'white' ? Math.max(0, materialDiff) : Math.max(0, -materialDiff)}
        />

        {/* OPENING + THINKING */}
        {(() => {
          const opening = detectOpening(game.moveHistory);
          return opening ? <div style={styles.openingTag}>{opening}</div> : null;
        })()}
        {botThinking && game.status === 'active' && !game.paused && (
          <div style={styles.thinkingTag}>Bot inafikiri…</div>
        )}

        {/* MOVE LIST (toggled from menu) */}
        {showMoveList && game.moveHistory && game.moveHistory.length > 0 && (
          <div style={styles.moveListPanel}>
            <div style={styles.moveListInner}>
              {Array.from({ length: Math.ceil(game.moveHistory.length / 2) }).map((_, i) => {
                const white = game.moveHistory[i * 2];
                const black = game.moveHistory[i * 2 + 1];
                return (
                  <div key={i} style={styles.moveRow}>
                    <span style={styles.moveNum}>{i + 1}.</span>
                    <span style={styles.moveSan}>{white?.san}</span>
                    <span style={styles.moveSan}>{black?.san || ''}</span>
                  </div>
                );
              })}
              <div ref={moveListEndRef} />
            </div>
          </div>
        )}

        {/* PRIMARY ACTION BAR — only the essentials stay visible */}
        {isPlayer && (game.status === 'active' || game.status === 'waiting') && (
          <div style={styles.actionBar}>
            {game.isBotGame && game.status === 'active' && (
              <button
                style={styles.actionChip}
                onClick={handleHint}
                disabled={hintsLeft <= 0 || !interactive}
              >
                <Icon name="sparkle" size={15} color={hintsLeft > 0 && interactive ? '#34D399' : 'rgba(255,255,255,0.25)'} />
                <span>Hint · {hintsLeft}</span>
              </button>
            )}
            {game.isBotGame && game.status === 'active' && game.moveHistory?.length > 0 && (
              <button style={styles.actionChip} onClick={handleBotUndo}>
                <Icon name="clock" size={15} color="rgba(236,239,234,0.72)" />
                <span>Rudisha</span>
              </button>
            )}
            {game.isBotGame && game.status === 'active' && (
              <button
                style={{ ...styles.actionChip, ...(game.paused ? styles.actionChipActive : {}) }}
                onClick={game.paused ? handleResume : handlePause}
              >
                <Icon name={game.paused ? 'play' : 'lock'} size={15} color={game.paused ? '#34D399' : 'rgba(236,239,234,0.72)'} />
                <span>{game.paused ? 'Endelea' : 'Simamisha'}</span>
              </button>
            )}
            {/* The menu holds everything else */}
            <button style={styles.menuBtn} onClick={() => setMenuOpen(true)} aria-label="Menu">
              <span style={styles.menuDots}>⋯</span>
            </button>
          </div>
        )}

        {hint && (
          <div style={styles.hintText}>
            Coach: jaribu <strong>{hint.san}</strong> ({hint.from} → {hint.to})
          </div>
        )}

        {/* DRAW OFFER — received */}
        {game.drawOfferedBy && isPlayer && game.status === 'active' && game.drawOfferedBy !== myColor && (
          <div style={styles.offerBanner}>
            Mpinzani amependekeza sare (draw).
            <div style={styles.offerBtnRow}>
              <button style={styles.offerAcceptBtn} onClick={handleAcceptDraw}>Kubali</button>
              <button style={styles.offerCancelBtn} onClick={handleDeclineDraw}>Kataa</button>
            </div>
          </div>
        )}

        {/* TAKEBACK — received */}
        {game.takebackRequestedBy && isPlayer && game.status === 'active' && game.takebackRequestedBy !== myColor && (
          <div style={styles.offerBanner}>
            Mpinzani anaomba kurudisha move ya mwisho.
            <div style={styles.offerBtnRow}>
              <button style={styles.offerAcceptBtn} onClick={handleAcceptTakeback}>Kubali</button>
              <button style={styles.offerCancelBtn} onClick={handleDeclineTakeback}>Kataa</button>
            </div>
          </div>
        )}
      </div>

      {/* GAME MENU SHEET */}
      {menuOpen && (
        <div style={styles.sheetOverlay} onClick={() => setMenuOpen(false)}>
          <div style={styles.sheet} onClick={e => e.stopPropagation()}>
            <div style={styles.sheetHandle} />
            <div style={styles.sheetTitle}>Menu ya Mchezo</div>

            <div style={styles.sheetGrid}>
              <button style={styles.sheetItem} onClick={() => { setManualFlip(f => !f); }}>
                <Icon name="world" size={20} color="#34D399" /><span>Geuza Ubao</span>
              </button>
              <button style={styles.sheetItem} onClick={() => { const n = toggleMute(); setMuted(n); }}>
                <Icon name={muted ? 'x' : 'bolt'} size={20} color="#34D399" /><span>{muted ? 'Washa Sauti' : 'Zima Sauti'}</span>
              </button>
              <button style={styles.sheetItem} onClick={() => setShowMoveList(v => !v)}>
                <Icon name="notes" size={20} color="#34D399" /><span>{showMoveList ? 'Ficha Moves' : 'Onyesha Moves'}</span>
              </button>
              <button style={styles.sheetItem} onClick={() => setBoardTheme(nextBoardTheme())}>
                <Icon name="dashboard" size={20} color="#34D399" /><span>Ubao: {boardTheme.name}</span>
              </button>
              <button style={styles.sheetItem} onClick={() => setPieceTheme(nextPieceTheme())}>
                <Icon name="chess" size={20} color="#34D399" /><span>Vipande: {pieceTheme.name}</span>
              </button>
              {game.status === 'active' && !game.isBotGame && !game.drawOfferedBy && (
                <button style={styles.sheetItem} onClick={() => { handleOfferDraw(); setMenuOpen(false); }}>
                  <Icon name="heart" size={20} color="#34D399" /><span>Pendekeza Sare</span>
                </button>
              )}
              {game.status === 'active' && !game.isBotGame && !game.takebackRequestedBy && game.moveHistory?.length > 0 && (
                <button style={styles.sheetItem} onClick={() => { handleRequestTakeback(); setMenuOpen(false); }}>
                  <Icon name="clock" size={20} color="#34D399" /><span>Omba Takeback</span>
                </button>
              )}
            </div>

            <div style={styles.sheetDanger}>
              {game.status === 'active' && !game.isBotGame && (
                <button style={styles.sheetResign} onClick={() => { handleResign(); setMenuOpen(false); }}>
                  Jisalimishe (Resign)
                </button>
              )}
              <button style={styles.sheetQuit} onClick={() => { handleQuit(); setMenuOpen(false); }}>
                {game.isBotGame ? 'Toka kwenye Mazoezi' : game.status === 'waiting' ? 'Ghairi Mchezo' : 'Ondoka'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div key={toast.id} style={styles.toast}>{toast.text}</div>
      )}

    </div>
  );
}

const CAPTURE_GLYPHS = { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛' };

function PlayerPanel({ name, rating, tier, time, isTurn, placeholder, captured, materialEdge }) {
  return (
    <div style={{ ...styles.playerPanel, ...(isTurn ? styles.playerPanelActive : {}) }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.panelName}>{name || placeholder || 'Player'}</div>
        {rating != null && tier && (
          <div style={{ ...styles.panelTier, color: tier.color }}>{tier.swahili} · {rating}</div>
        )}
        {captured && captured.length > 0 && (
          <div style={styles.capturedRow}>
            {captured.map((t, i) => (
              <span key={i} style={styles.capturedGlyph}>{CAPTURE_GLYPHS[t]}</span>
            ))}
            {materialEdge > 0 && <span style={styles.materialEdge}>+{materialEdge}</span>}
          </div>
        )}
      </div>
      {time != null && (
        <div style={{ ...styles.clock, ...(isTurn ? styles.clockActive : {}) }}>
          {fmtClock(time)}
        </div>
      )}
    </div>
  );
}

// ── Light "Sage & Cream" theme — green softened with white, per Iyanna's
// request: the deep green felt heavy, so the play screen now mixes the
// brand green with white for a calm, high-quality look.
const styles = {
  page: { minHeight: '100vh', background: 'radial-gradient(circle at 30% 0%, #262D2A 0%, #1B201E 48%, #121614 100%)', padding: '18px 14px 50px' },
  topBar: { maxWidth: '560px', margin: '0 auto 14px', display: 'flex', alignItems: 'center', gap: '10px' },
  backLink: { color: 'rgba(236,239,234,0.72)', fontSize: '13px', textDecoration: 'none', fontWeight: '600' },
  backIconBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  topCenter: { flex: 1, display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' },
  chip: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(236,239,234,0.75)', fontSize: '11px', fontWeight: '700', padding: '5px 12px', borderRadius: '99px' },
  codeChip: { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)', color: '#34D399', fontSize: '11px', fontWeight: '700', padding: '5px 12px', borderRadius: '99px' },
  codeBox: { color: '#34D399', fontSize: '13px', background: 'rgba(52,211,153,0.14)', border: '1px solid rgba(52,211,153,0.35)', padding: '6px 12px', borderRadius: '9px', fontWeight: '600' },
  shareBtn: { marginLeft: 'auto', background: '#232927', border: '1px solid rgba(255,255,255,0.14)', color: '#D9F5E7', padding: '7px 14px', borderRadius: '9px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' },
  openingTag: { textAlign: 'center', color: 'rgba(236,239,234,0.58)', fontSize: '12px', marginTop: '6px', fontWeight: '600' },
  coachRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' },
  hintBtn: { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.45)', color: '#34D399', padding: '8px 16px', borderRadius: '9px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  hintText: { color: '#34D399', fontSize: '12.5px', textAlign: 'center', fontWeight: '600' },
  thinkingTag: { textAlign: 'center', color: '#34D399', fontSize: '12.5px', marginTop: '6px', fontStyle: 'italic' },
  actionBtnRow: { display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '8px', flexWrap: 'wrap' },
  actionBar: { display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' },
  actionChip: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#232927', border: '1px solid rgba(255,255,255,0.14)', color: '#ECEFEA', padding: '11px 16px', borderRadius: '13px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 3px 10px rgba(0,0,0,0.3)' },
  actionChipActive: { background: 'rgba(52,211,153,0.14)', borderColor: 'rgba(52,211,153,0.5)', color: '#34D399' },
  menuBtn: { width: '46px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#232927', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '13px', cursor: 'pointer', boxShadow: '0 3px 10px rgba(0,0,0,0.3)' },
  menuDots: { color: '#D9F5E7', fontSize: '24px', fontWeight: '800', lineHeight: 1, marginTop: '-6px' },
  sheetOverlay: { position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '480px', background: 'linear-gradient(180deg, #262D2A, #1E2421)', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', borderBottom: 'none', padding: '10px 18px calc(24px + env(safe-area-inset-bottom))', boxShadow: '0 -14px 40px rgba(0,0,0,0.5)' },
  sheetHandle: { width: '40px', height: '4px', borderRadius: '99px', background: 'rgba(255,255,255,0.18)', margin: '4px auto 14px' },
  sheetTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '17px', color: '#D9F5E7', fontWeight: '700', textAlign: 'center', marginBottom: '16px' },
  sheetGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' },
  sheetItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', background: '#2B322E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '15px 8px', cursor: 'pointer', fontFamily: 'inherit', color: 'rgba(236,239,234,0.88)', fontSize: '11px', fontWeight: '700', textAlign: 'center' },
  sheetDanger: { display: 'flex', gap: '10px' },
  sheetResign: { flex: 1, background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.4)', color: '#F87171', padding: '14px', borderRadius: '13px', fontSize: '13.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  sheetQuit: { flex: 1, background: '#2B322E', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(236,239,234,0.72)', padding: '14px', borderRadius: '13px', fontSize: '13.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  toast: { position: 'fixed', left: '50%', bottom: '110px', transform: 'translateX(-50%)', zIndex: 960, background: '#D9F5E7', border: '1px solid rgba(52,211,153,0.45)', color: '#06281A', padding: '13px 22px', borderRadius: '13px', fontSize: '13px', fontWeight: '700', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', maxWidth: '90%', textAlign: 'center' },
  quitBtn: { background: '#232927', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(236,239,234,0.72)', padding: '10px 20px', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },
  spectatorTag: { color: 'rgba(236,239,234,0.58)', fontSize: '12px', background: '#232927', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '8px' },
  roomLayout: { maxWidth: '560px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '10px' },
  boardWrapper: { position: 'relative' },
  playerPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '10px 14px', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' },
  playerPanelActive: { border: '1.5px solid rgba(52,211,153,0.7)', background: 'linear-gradient(135deg, #2A332D, #243A2E)' },
  panelName: { color: '#ECEFEA', fontSize: '13.5px', fontWeight: '800' },
  panelTier: { fontSize: '11px', fontWeight: '700', marginTop: '2px' },
  clock: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '18px', color: 'rgba(236,239,234,0.88)', background: '#2B322E', padding: '6px 12px', borderRadius: '9px', minWidth: '64px', textAlign: 'center', fontWeight: '700' },
  clockActive: { color: '#34D399', background: 'rgba(52,211,153,0.18)' },
  resignBtn: { alignSelf: 'center', marginTop: '8px', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.4)', color: '#F87171', padding: '10px 20px', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },
  drawBtn: { background: '#232927', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(236,239,234,0.88)', padding: '10px 16px', borderRadius: '10px', fontSize: '12.5px', cursor: 'pointer', fontFamily: 'inherit' },
  toolbarRow: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2px' },
  toolBtnActive: { background: 'rgba(52,211,153,0.16)', borderColor: '#34D399', color: '#34D399' },
  pausedOverlay: { position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(11,38,26,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '14px', padding: '16px' },
  pausedCard: { textAlign: 'center', maxWidth: '300px' },
  pausedTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '21px', color: '#fff', fontWeight: '700', marginBottom: '8px' },
  pausedSub: { color: 'rgba(255,255,255,0.7)', fontSize: '12.5px', lineHeight: 1.6, marginBottom: '16px' },
  resumeBtn: { background: 'linear-gradient(135deg, #6EE7A8, #22C55E 55%, #0E9F6E)', color: '#06281A', border: 'none', padding: '13px 26px', borderRadius: '11px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 22px rgba(16,185,129,0.35)' },
  toolBtn: { background: '#232927', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(236,239,234,0.72)', padding: '7px 12px', borderRadius: '8px', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' },
  moveListPanel: { background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '10px 12px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)' },
  moveListInner: { maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' },
  moveRow: { display: 'flex', gap: '10px', fontSize: '12.5px', color: 'rgba(236,239,234,0.88)', fontFamily: 'monospace' },
  moveNum: { color: 'rgba(236,239,234,0.4)', width: '24px', flexShrink: 0 },
  moveSan: { minWidth: '54px' },
  capturedRow: { display: 'flex', alignItems: 'center', gap: '1px', marginTop: '4px', flexWrap: 'wrap' },
  capturedGlyph: { fontSize: '13px', color: 'rgba(236,239,234,0.5)', lineHeight: 1 },
  materialEdge: { fontSize: '11px', color: '#3FBE83', fontWeight: '800', marginLeft: '5px' },
  offerBanner: { background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '12px 14px', textAlign: 'center', color: '#ECEFEA', fontSize: '13px', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' },
  offerBtnRow: { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '10px' },
  offerAcceptBtn: { background: '#10B981', border: 'none', color: '#fff', padding: '8px 18px', borderRadius: '8px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  offerCancelBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(236,239,234,0.72)', padding: '8px 18px', borderRadius: '8px', fontSize: '12.5px', cursor: 'pointer', fontFamily: 'inherit', marginLeft: '8px' },
  confettiWrap: { position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', borderRadius: '14px' },
  confettiPiece: { position: 'absolute', top: '-10%', width: '8px', height: '14px', opacity: 0.9, animationName: 'iyannaConfettiFall', animationTimingFunction: 'linear', animationIterationCount: 1, animationFillMode: 'forwards' },
  resultOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(15,35,24,0.55)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 20, padding: '16px', borderRadius: '14px',
  },
  resultModal: {
    width: '100%', maxWidth: '340px', maxHeight: '92%', overflowY: 'auto',
    textAlign: 'center', background: 'linear-gradient(180deg, #2A302D, #202623)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '18px', padding: '22px 16px',
    boxShadow: '0 18px 50px rgba(10,25,17,0.4)',
    animation: 'iyannaResultPop 0.28s cubic-bezier(0.34, 1.4, 0.64, 1)',
  },
  resultTitle: { color: '#D9F5E7', fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', marginBottom: '4px', fontWeight: '700' },
  resultSubtitle: { color: 'rgba(236,239,234,0.58)', fontSize: '13px', marginBottom: '8px' },
  resultReason: { color: 'rgba(236,239,234,0.45)', fontSize: '12.5px', marginBottom: '16px' },
  lessonCard: { background: 'linear-gradient(135deg, rgba(52,211,153,0.12), rgba(52,211,153,0.05))', border: '1px solid rgba(52,211,153,0.4)', borderRadius: '14px', padding: '16px', marginBottom: '18px', textAlign: 'left' },
  lessonBadge: { fontSize: '10.5px', fontWeight: '800', color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  bonusNote: { display: 'inline-block', marginTop: '10px', background: 'linear-gradient(135deg, rgba(110,231,168,0.18), rgba(52,211,153,0.08))', border: '1px solid rgba(52,211,153,0.4)', color: '#6EE7A8', fontSize: '12.5px', fontWeight: '800', padding: '9px 16px', borderRadius: '11px' },
  saveRow: { marginTop: '14px' },
  saveBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A', border: 'none', padding: '12px 20px', borderRadius: '12px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  saveBtnDone: { background: 'rgba(52,211,153,0.15)', color: '#6EE7A8', border: '1px solid rgba(52,211,153,0.4)' },
  saveHint: { color: 'rgba(236,239,234,0.5)', fontSize: '11px', marginTop: '7px' },
  lessonSwahili: { color: '#ECEFEA', fontSize: '14px', fontWeight: '700', marginBottom: '4px', lineHeight: 1.5 },
  lessonEnglish: { color: 'rgba(236,239,234,0.5)', fontSize: '12px', fontStyle: 'italic', lineHeight: 1.5 },
  resultBtnRow: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' },
  rematchReady: { marginTop: '12px', marginBottom: '4px' },
  reviewBtn: { background: '#232927', border: '1.5px solid #D9F5E7', color: '#D9F5E7', padding: '11px 20px', borderRadius: '11px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  newGameBtn: { background: 'linear-gradient(135deg, #D9F5E7, #10B981)', border: 'none', color: '#fff', padding: '11px 22px', borderRadius: '11px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 18px rgba(16,185,129,0.3)' },
  centerMsg: { color: '#ECEFEA', textAlign: 'center', padding: '80px 20px', fontSize: '14px' },
  link: { color: '#34D399', fontWeight: '700' },
};
